import React from "react";
import { Typography, theme, Tag, Flex, Card, Divider } from "antd";
import {
  BarcodeOutlined,
  DollarOutlined,
  PercentageOutlined,
  ShopOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import type { ProductData } from "../types";

const { Title, Text } = Typography;

interface ProductDetailsProps {
  product: ProductData;
}

// ─── FieldItem ────────────────────────────────────────────────────────────────

const FieldItem = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  const { token } = theme.useToken();
  return (
    <Flex vertical gap={3}>
      <Text
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: token.colorTextSecondary,
        }}
      >
        {label}
      </Text>
      {children}
    </Flex>
  );
};

// ─── SectionCard ─────────────────────────────────────────────────────────────

const SectionCard = ({
  icon,
  title,
  iconBg,
  iconColor,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  iconBg: string;
  iconColor: string;
  children: React.ReactNode;
}) => {
  const { token } = theme.useToken();
  return (
    <Card
      style={{ borderRadius: token.borderRadiusLG, height: "100%" }}
      styles={{ body: { padding: 24 } }}
      hoverable
    >
      <Flex vertical gap={18}>
        <Flex align="center" gap={12}>
          <Flex
            align="center"
            justify="center"
            style={{
              width: 40,
              height: 40,
              borderRadius: token.borderRadiusLG,
              background: iconBg,
              color: iconColor,
              fontSize: 18,
              flexShrink: 0,
            }}
          >
            {icon}
          </Flex>
          <Title level={5} style={{ margin: 0, fontWeight: 800, fontSize: 16 }}>
            {title}
          </Title>
        </Flex>
        <Divider style={{ margin: 0 }} />
        <Flex vertical gap={16}>
          {children}
        </Flex>
      </Flex>
    </Card>
  );
};

const ProductDetails: React.FC<ProductDetailsProps> = ({ product }) => {
  const { token } = theme.useToken();
  return (
    <div style={{ padding: "0 8px 32px" }}>
      <Flex vertical gap={24}>
        {/* ── Profile Header ── */}
        <Flex vertical align="center" gap={10} style={{ textAlign: "center" }}>
          <Title
            level={2}
            style={{
              margin: 0,
              fontWeight: 900,
              fontSize: 26,
              letterSpacing: "-0.02em",
              color: token.colorText,
            }}
          >
            {product.product_name}
          </Title>

          <Tag
            color="orange"
            icon={<ShoppingOutlined />}
            style={{
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              borderRadius: 999,
              margin: 0,
            }}
          >
            Producto / Servicio
          </Tag>
        </Flex>

        {/* ── Info Card ── */}
        <div style={{ maxWidth: 600, margin: "0 auto", width: "100%" }}>
          <SectionCard
            icon={<BarcodeOutlined />}
            title={`Detalles del ${product.product_type}`}
            iconBg={token.colorWarningBg}
            iconColor={token.colorWarning}
          >
            {product.product_description && (
              <FieldItem label="Descripción">
                <Text
                  style={{
                    fontSize: 14,
                    color: token.colorText,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {product.product_description}
                </Text>
              </FieldItem>
            )}

            <FieldItem label="Clave SAT">
              <Text
                strong
                copyable={
                  product.product_key_sat
                    ? { text: product.product_key_sat }
                    : false
                }
                style={{
                  fontSize: 16,
                  fontWeight: 900,
                  color: token.colorText,
                  letterSpacing: "0.05em",
                }}
              >
                {product.product_key_sat || "---"}
              </Text>
            </FieldItem>

            <FieldItem label="Precio Unitario">
              <Flex align="center" gap={8}>
                <DollarOutlined style={{ color: token.colorSuccess }} />
                <Text
                  strong
                  style={{
                    fontSize: 18,
                    color: token.colorText,
                  }}
                >
                  ${Number(product.product_price).toFixed(2)}
                </Text>
              </Flex>
            </FieldItem>


            <FieldItem label="Precio venta">
              <Flex align="center" gap={8}>
                <DollarOutlined style={{ color: token.colorSuccess }} />
                <Text
                  strong
                  style={{
                    fontSize: 18,
                    color: token.colorText,
                  }}
                >
                  ${Number(product.product_price_sale).toFixed(2)}
                </Text>
              </Flex>
            </FieldItem>



            <FieldItem label="IVA">
              <Flex align="center" gap={8}>
                <DollarOutlined style={{ color: token.colorPrimary }} />
                <Text
                  style={{
                    fontSize: 15,
                    color: token.colorText,
                    fontWeight: 600,
                  }}
                >
                  {Number(product.product_iva).toFixed(2)}
                </Text>
              </Flex>
            </FieldItem>

            <FieldItem label="Porcentaje IVA">
              <Flex align="center" gap={8}>
                <PercentageOutlined style={{ color: token.colorPrimary }} />
                <Text
                  style={{
                    fontSize: 15,
                    color: token.colorText,
                    fontWeight: 600,
                  }}
                >
                  {product.product_iva_percentage &&
                    `${Number(product.product_iva_percentage).toFixed(2)}%`
                  }
                </Text>
              </Flex>
            </FieldItem>
          </SectionCard>
        </div>
      </Flex>
    </div>
  );
};

export default ProductDetails;

